import {
  Address,
  CollectBillingAddressType,
  MerchantConfig,
  PaymentInstrument
} from 'types/api/intent';

function isMoreAddressInfoRequired(address: Address, constraint: CollectBillingAddressType) {
  switch (constraint) {
    case 'FULL':
      return !address.line1;
    case 'CNTRY':
      return !address.countryCode;
    case 'POSTALCNTRY':
      return !address.postalCode;
    default:
      return false;
  }
}

export function getCardError(card: PaymentInstrument, merchantConfig: MerchantConfig) {
  const merchantCountryCode = merchantConfig.merchantCountryCode;
  const paymentTypes = merchantConfig.paymentConstraints?.paymentTypes?.paymentType ?? [];
  const merchantSupportedCardBrands = paymentTypes.map(pType => pType.cardBrand);
  const allBrandsSupported = merchantSupportedCardBrands.length === 0;

  // based on RXO behavior, when non-BR merchant doesn't support VISA or BR
  // merchant doesn't support Visa and Electron, card is combo card. Error
  // message contains card type.
  if (card.paymentType.cardBrand === 'ELO') {
    // merchant in non-BR
    if (merchantCountryCode !== 'BR') {
      if (!merchantSupportedCardBrands.includes('ELO')) {
        return 'merchant_unsupported_card_brand';
      }
    } else if (!allBrandsSupported && !merchantSupportedCardBrands.includes('ELO')) {
      // merchant is BR
      return 'merchant_unsupported_card_brand';
    }
  }

  const isComboCard = card.combinationCard === 'A' || card.combinationCard === 'C';
  const noBrandsSupported =
    merchantCountryCode !== 'BR' &&
    merchantSupportedCardBrands.length === 1 &&
    merchantSupportedCardBrands[0] === 'ELECTRON';

  const comboCardError =
    isComboCard &&
    (noBrandsSupported ||
      (!allBrandsSupported &&
        !merchantSupportedCardBrands.includes('VISA') &&
        (merchantCountryCode !== 'BR' || !merchantSupportedCardBrands.includes('ELECTRON'))));

  if (comboCardError) {
    return 'merchant_unsupported_card_brand_type';
  }

  const filteredSupportedCardBrands =
    merchantCountryCode === 'BR'
      ? merchantSupportedCardBrands
      : merchantSupportedCardBrands.filter(cardBrand => cardBrand !== 'ELECTRON');

  const isComboCardSupported = isComboCard && filteredSupportedCardBrands.includes('ELECTRON');
  const isInvalidCardBrand =
    noBrandsSupported ||
    (!allBrandsSupported &&
      !(filteredSupportedCardBrands.includes(card.paymentType.cardBrand) || isComboCardSupported));

  if (isInvalidCardBrand) {
    return 'merchant_unsupported_card_brand';
  }

  const merchantCanadaVisaDebitSupport =
    merchantConfig.paymentConstraints?.acceptCanadianVISADebit ?? true;

  const isCaVisaDebitCard =
    card.billingAddress.countryCode === 'CA' &&
    card.paymentType.cardBrand === 'VISA' &&
    card.paymentType.cardType.toLowerCase() === 'debit';

  if (!merchantCanadaVisaDebitSupport && isCaVisaDebitCard) {
    // merchantCanadaVisaDebitSupport defaulted to true in get-card-list since paymentConstraints are optional in merchantConfig.
    // Followed above condition as in RXO
    return 'unsupported_canadian_card';
  }

  const acceptedRegions = merchantConfig.billingConstraints?.acceptedRegions?.region ?? [];
  const merchantSupportedBillingCountries = acceptedRegions.map(region => region.countryCode);

  // billingAccepted property is optional, in which case default to true
  const isBillingAddressAccepted = card.billingAddress.billingAccepted ?? true;
  const invalidBillingCountry =
    merchantSupportedBillingCountries.length > 0 &&
    !merchantSupportedBillingCountries.includes(card.billingAddress.countryCode);

  if (!isBillingAddressAccepted || invalidBillingCountry) {
    return 'unsupported_billing_address';
  }

  if (card.expired) {
    return 'card_expired';
  }

  if (card.verificationStatus === 'NOT_VERIFIED') {
    return 'card_not_verified';
  }

  if (
    isMoreAddressInfoRequired(
      card.billingAddress,
      merchantConfig.billingConstraints?.collectBillingAddress
    )
  ) {
    return 'partial_billing_address';
  }

  return null;
}
